<template>
  <div class="gallery-filters visitor-theme">
    <!-- Desktop: search, status, size, then the artist's groups (same row if all fit) -->
    <div v-if="!isMobile" class="filters-main filters-main--dropdowns" :class="`filters--n${dropdownCount}`">
      <div class="filter-search-wrap">
        <input v-model="localSearch" type="text" class="filter-search" placeholder="Search artworks…"
          @input="$emit('update:search', localSearch)">
        <button v-if="localSearch" class="filter-search-clear" tabindex="-1" title="Clear search" aria-label="Clear search"
          @click="localSearch = ''; $emit('update:search', '')">
          ×
        </button>
      </div>
      <div class="filter-practical">
        <FilterDropdown v-model="localAvailability" :options="['available', 'sold']" placeholder="status" show-all
          @update:model-value="$emit('update:availability', $event)" />
        <SizeFilterDropdown v-if="sizeOptions.length" :size="localSize" :options="sizeOptions" :size-range="sizeRange"
          :unit="sizeUnitLabel" @update:size="setSize" @update:size-range="setSizeRange" />
      </div>
      <div v-if="nonEmptyGroups.length" class="filter-groups">
        <FilterDropdown v-for="group in nonEmptyGroups" :key="group.id" :model-value="selectedFor(group.id)"
          :options="optionPairs(group)" :placeholder="group.label" multiple show-all
          @update:model-value="setGroupSelection(group.id, $event)" />
      </div>
    </div>

    <!-- Mobile: stacked rows -->
    <div v-if="isMobile" class="mobile-layout">
      <!-- Row 1: search + filters toggle (always) -->
      <div class="filters-top">
        <div class="filter-search-wrap">
          <input v-model="localSearch" type="text" class="filter-search" placeholder="Search…"
            @input="$emit('update:search', localSearch)">
          <button v-if="localSearch" class="filter-search-clear" tabindex="-1" title="Clear search" aria-label="Clear search"
            @click="localSearch = ''; $emit('update:search', '')">
            ×
          </button>
        </div>
        <button class="filters-toggle" :title="filtersOpen ? 'Hide filters' : 'Show filters'"
          :aria-label="filtersOpen ? 'Hide filters' : 'Show filters'" @click="filtersOpen = !filtersOpen">
          <FilterIcon class="toggle-icon" />
          <span>{{ filtersOpen ? 'hide filters' : 'show filters' }}</span>
        </button>
      </div>

      <template v-if="filtersOpen">
        <!-- Status, size, then the artist's groups, one flowing grid -->
        <div class="mobile-row">
          <FilterDropdown v-model="localAvailability" :options="['available', 'sold']" placeholder="status" show-all
            @update:model-value="$emit('update:availability', $event)" />
          <SizeFilterDropdown v-if="sizeOptions.length" :size="localSize" :options="sizeOptions" :size-range="sizeRange"
            :unit="sizeUnitLabel" @update:size="setSize" @update:size-range="setSizeRange" />
          <FilterDropdown v-for="group in nonEmptyGroups" :key="group.id" :model-value="selectedFor(group.id)"
            :options="optionPairs(group)" :placeholder="group.label" multiple show-all
            @update:model-value="setGroupSelection(group.id, $event)" />
        </div>
      </template>
    </div>

    <!-- Count row: shown on all sizes -->
    <div class="filters-count-row">
      <span class="filter-count">
        <!-- Only the …→count swap dissolves (key flips once); filter-driven
             changes patch in place with no animation -->
        <span class="filter-count-slot">
          <Transition name="count-fade">
            <span :key="countPending ? 'pending' : 'value'" class="filter-count-number"
              :class="{ 'is-pending': countPending }">{{ countPart }}</span>
          </Transition>
        </span>
        <span class="filter-count-unit">{{ countUnit }}</span>
        <template v-if="isActive && fullyLoaded"> <button class="filter-clear filter-clear-inline"
            title="Clear all filters" aria-label="Clear all filters" @click="clearAll">Clear</button></template>
      </span>
    </div>
  </div>
</template>

<script>
import FilterDropdown from '@/components/gallery/FilterDropdown.vue'
import FilterIcon from '@/assets/icons/filter.vue'
import SizeFilterDropdown from '@/components/gallery/SizeFilterDropdown.vue'
import { EMPTY_SIZE_RANGE, hasSizeRange } from '@/utils/sizeUnits'
import { isInPreview } from '@/utils/pageWidth'

export default {
  name: 'GalleryFilters',

  components: { FilterDropdown, FilterIcon, SizeFilterDropdown },

  props: {
    tagGroups: { type: Array, default: () => [] },
    // { [groupLabel]: string[] }
    selected: { type: Object, default: () => ({}) },
    search: { type: String, default: '' },
    availability: { type: String, default: '' },
    size: { type: String, default: '' },
    // [{ value, text, range }]; empty hides the size filter
    sizeOptions: { type: Array, default: () => [] },
    // { minWidth, maxWidth, minHeight, maxHeight } in the visitor's unit
    sizeRange: { type: Object, default: () => ({ ...EMPTY_SIZE_RANGE }) },
    sizeUnitLabel: { type: String, default: 'cm' },
    count: { type: Number, default: 0 },
    totalCount: { type: Number, default: null },
    fullyLoaded: { type: Boolean, default: true },
    open: { type: Boolean, default: false }
  },

  emits: ['update:search', 'update:selected', 'update:availability', 'update:size', 'update:size-range', 'update:open', 'clear'],

  data() {
    return {
      MOBILE_QUERY: '(max-width: 768px)',
      filtersOpen: this.open,
      localSearch: this.search,
      localAvailability: this.availability,
      localSize: this.size,
      isMobile: window.matchMedia('(max-width: 768px)').matches
    }
  },

  computed: {
    nonEmptyGroups() {
      return this.tagGroups.filter(g => g && Array.isArray(g.tags) && g.tags.length)
    },
    // Groups + status + size; picks the one-row width in the styles
    dropdownCount() {
      return this.nonEmptyGroups.length + 1 + (this.sizeOptions.length ? 1 : 0)
    },
    hasSelectedTags() {
      return Object.values(this.selected || {}).some(arr => Array.isArray(arr) && arr.length > 0)
    },
    isActive() {
      return !!(this.localSearch || this.hasSelectedTags || this.localAvailability || this.localSize || hasSizeRange(this.sizeRange))
    },
    countPending() {
      return !this.fullyLoaded && this.totalCount === null
    },
    // '…' | '271' | '5 of 271'
    countPart() {
      if (this.countPending) return '…'
      if (this.isActive && this.fullyLoaded) {
        return `${this.count} of ${this.totalCount ?? this.count}`
      }
      return `${this.totalCount ?? this.count}`
    },
    countUnit() {
      const n = this.isActive && this.fullyLoaded ? this.count : (this.totalCount ?? this.count)
      return n === 1 ? 'work' : 'works'
    }
  },

  watch: {
    open(val) { if (val !== this.filtersOpen) this.filtersOpen = val },
    filtersOpen(val) { this.$emit('update:open', val) },
    // Sync external prop changes (e.g. parent-driven clear) into local state
    search(val) { this.localSearch = val },
    availability(val) { this.localAvailability = val },
    size(val) { this.localSize = val }
  },

  mounted() {
    // Admin previews always show the desktop page
    if (isInPreview(this.$el)) {
      this.isMobile = false
      return
    }
    this.mediaQuery = window.matchMedia(this.MOBILE_QUERY)
    this.handleMediaChange = (e) => { this.isMobile = e.matches }
    this.mediaQuery.addEventListener('change', this.handleMediaChange)
  },

  beforeUnmount() {
    this.mediaQuery?.removeEventListener('change', this.handleMediaChange)
  },

  methods: {
    optionPairs(group) {
      return group.tags.map(t => ({ text: t.label, value: t.id }))
    },
    selectedFor(groupId) {
      const val = this.selected?.[groupId]
      return Array.isArray(val) ? val : []
    },
    setGroupSelection(groupId, tagIds) {
      this.$emit('update:selected', { ...this.selected, [groupId]: tagIds })
    },
    // A bracket and a custom size replace each other
    setSize(val) {
      this.localSize = val
      this.$emit('update:size', val)
      if (hasSizeRange(this.sizeRange)) this.$emit('update:size-range', { ...EMPTY_SIZE_RANGE })
    },
    setSizeRange(range) {
      if (this.localSize) {
        this.localSize = ''
        this.$emit('update:size', '')
      }
      this.$emit('update:size-range', range)
    },
    clearAll() {
      this.localSearch = ''
      this.localAvailability = ''
      this.localSize = ''
      this.$emit('clear')
    }
  }
}
</script>

<style scoped>
.gallery-filters {
  position: relative;
  z-index: 20;  /* older browsers make a container its own stacking layer; keep open dropdowns above the artworks */
  margin-bottom: 0;
  container: gallery-filters / inline-size;
}

/* Desktop: search, then dropdowns; left-aligned to match gallery */
.filters-main--dropdowns {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0.85rem 0.75rem;
  /* row-gap (between wrapped rows) / column-gap (between filters) */
}

/* Groups: an even grid on the row below, or inline at 140px when everything fits (set below) */
.filters-main--dropdowns .filter-search-wrap {
  flex: 1 1 12rem;
  min-width: 12rem;
}

.filters-main--dropdowns .filter-search {
  width: 100%;
}

.filter-practical,
.filter-groups {
  display: grid;
  gap: 0.85rem 0.75rem;
  grid-auto-columns: 140px;
}

.filter-practical {
  flex: 0 0 auto;
  grid-auto-flow: column;
}

.filter-groups {
  flex: var(--groups-flex, 1 0 100%);
  grid-template-columns: var(--groups-cols, repeat(auto-fit, minmax(140px, 1fr)));
  grid-auto-flow: var(--groups-flow, row);
}

.filter-practical .filter-dropdown,
.filter-groups .filter-dropdown {
  min-width: 0;
}

/* One row when search (12rem = 192px) + 140px and a 12px gap per dropdown fit the bar */
@container gallery-filters (min-width: 344px) { .filters--n1 { --groups-flex: 0 0 auto; --groups-cols: none; --groups-flow: column; } }
@container gallery-filters (min-width: 496px) { .filters--n2 { --groups-flex: 0 0 auto; --groups-cols: none; --groups-flow: column; } }
@container gallery-filters (min-width: 648px) { .filters--n3 { --groups-flex: 0 0 auto; --groups-cols: none; --groups-flow: column; } }
@container gallery-filters (min-width: 800px) { .filters--n4 { --groups-flex: 0 0 auto; --groups-cols: none; --groups-flow: column; } }
@container gallery-filters (min-width: 952px) { .filters--n5 { --groups-flex: 0 0 auto; --groups-cols: none; --groups-flow: column; } }
@container gallery-filters (min-width: 1104px) { .filters--n6 { --groups-flex: 0 0 auto; --groups-cols: none; --groups-flow: column; } }
@container gallery-filters (min-width: 1256px) { .filters--n7 { --groups-flex: 0 0 auto; --groups-cols: none; --groups-flow: column; } }
@container gallery-filters (min-width: 1408px) { .filters--n8 { --groups-flex: 0 0 auto; --groups-cols: none; --groups-flow: column; } }

.mobile-layout {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 0.75rem;
  font-size: 0.8rem;
}

.filters-top {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  align-items: center;
}

.filters-top .filter-search-wrap,
.filters-top .filter-search {
  width: 100%;
}

.mobile-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  align-items: center;
}

.mobile-count-collapsed {
  text-align: center;
}

.mobile-count-collapsed .filter-count {
  margin-left: 0;
}

.filter-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.filter-search-clear {
  position: absolute;
  right: 0.5rem;
  padding: 0;
  border: none;
  background: transparent;
  color: color-mix(in srgb, var(--color-text) 60%, var(--color-bg));
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  transition: color 0.15s;

  @media (hover: hover) {
    &:hover {
      color: var(--color-text);
    }
  }
}

.filter-search {
  padding: 0.3rem 1.75rem 0.3rem 0.7rem;
  border: none;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-text) 20%, transparent);
  border-radius: 8px;
  background: transparent;
  color: var(--color-text);
  font: inherit;
  font-size: 0.875rem;
  width: 160px;
  line-height: 1.4;
  transition: box-shadow 0.15s;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-highlight);
  }

  &::placeholder {
    color: color-mix(in srgb, var(--color-text) 60%, var(--color-bg));
  }
}

.filters-count-row {
  margin: 1rem 0;
  text-align: center;
}

.filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 12ch; /* stable pill size across the …→count swap */
  padding: 4px 14px;
  border-radius: 999px;
  background-color: var(--color-accent);
  color: var(--color-accent-text, #fff);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  white-space: nowrap;
}

/* Positioning context for the crossfade overlap */
.filter-count-slot {
  position: relative;
  display: inline-flex;
  justify-content: center;
  min-width: 3ch;
}

.filter-count-number,
.filter-count-unit {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.filter-count-unit {
  margin-left: 0.35em; /* flex items don't render whitespace between them */
}

.filter-count-number {
  text-align: center;
}

.filter-count-number.is-pending {
  opacity: 0.5; /* dimmed = provisional */
}

.count-fade-enter-active,
.count-fade-leave-active {
  transition: opacity 0.18s ease;
}

/* Leaving text out of flow so old and new overlap - a dissolve, not a swap */
.count-fade-leave-active {
  position: absolute;
  left: 0;
  right: 0;
}

.count-fade-enter-from,
.count-fade-leave-to {
  opacity: 0;
}

.filter-count-inline {
  text-align: center;
  justify-self: center;
  align-self: center;
  margin-left: 0;
}

.filter-clear {
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  margin-left: 4px;
  border: none;
  border-radius: 999px;
  background-color: var(--color-highlight);
  color: var(--color-highlight-text, #fff);
  font: inherit;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 0.15s;

  @media (hover: hover) {
    &:hover {
      opacity: 0.8;
    }
  }
}

.filters-toggle {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.3rem 0.75rem;
  border: none;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-text) 20%, transparent);
  border-radius: 8px;
  background: transparent;
  color: var(--color-text);
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.4;
  cursor: pointer;
  text-transform: lowercase;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;
  touch-action: manipulation;

  @media (hover: hover) {
    &:hover {
      box-shadow: 0 0 0 2px var(--color-highlight);
      color: var(--color-highlight);
    }
  }
}

.toggle-icon {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  pointer-events: none;
}
</style>
